import React, { useMemo, useState } from 'react'
import queryString from 'qs'
import flowRight from 'lodash/flowRight'
import styled from 'styled-components'
import { NoteTypeWithStatus } from 'shared/components/Notes/models/note'
import useQuery from 'shared/hooks/useQuery'
import withDialog, { WrappedProps } from 'shared/hocs/withDialog'
import { ConfirmationType } from 'shared/components/Notes/models/confirmation'
import TextField, { FieldLabel } from 'shared/components/TextField'
import Button from 'shared/components/Button'
import Box from 'shared/components/Box'
import CustomTooltip from 'shared/components/CustomTooltip'
import NoteItem from 'shared/components/Notes/components/NoteItem'
import { permissionSelector, roleSelector, authSelector } from 'shared/hocs/withAuth'
import { useDispatch, useSelector } from 'react-redux'
import Loader from 'shared/components/Loader'
import { UserRoleTypeWithStatus } from 'pages/UserRole/model/role'
import Avatar from 'pages/Parent/components/Avatar'
import { FLAG_WARNING_COLOR, FLAG_DANGER_COLOR } from 'shared/constants/participant'

type Props = WrappedProps<ConfirmationType> & {
  label?: string
  description?: string
  type: 'stop' | 'warn'
  participantId: string
}

function UnflagDialog(props: Props) {
  const {
    formState,
    formHandlers,
    onClose,
    onContinue,
    isProcessing,
    description,
    label,
    participantId,
    type = 'warn',
  } = props
  const { fields, errors } = formState
  const { onElementChange, onChange } = formHandlers
  const { auth: user } = useSelector(authSelector)

  const noteQueryString = useMemo(
    () =>
      queryString.stringify({
        participant_id: participantId,
        type,
      }),
    [participantId, type],
  )

  const [{ loading, data: noteData }, queryHandler] = useQuery<NoteTypeWithStatus | null>(
    { url: `/note/flag/user?${noteQueryString}` },
    {
      initialData: null,
      isBase: false,
      skip: false,
    },
  )

  if (loading) {
    return <Loader />
  }

  return (
    <UnflagDialogBodyContainer className={`confirm-dialog-container ${type}`}>
      {noteData && (
        <>
          <Title mb="20px">
            Resolve Flag
            <CustomTooltip
              placement="bottom"
              className="tooltip_flag"
              width="25"
              height="24"
              toopTipMaxWidth="380px"
              title={
                type == 'stop'
                  ? `This member can't check into the facility until the matter is resolved.`
                  : `This member has a reminder note on file`
              }
            />
          </Title>
          <Status mt="20px">{type == 'warn' ? 'Yellow' : 'Red'} Status</Status>
          <NoteContainer className={`${type}`}>
            <DescriptionContainer>
              <Description>{type == 'warn' ? 'Reminder' : 'Stop Member at Check-In'}</Description>
              {type === 'warn' && <Subdescription>(Member can still check-in)</Subdescription>}
            </DescriptionContainer>
            <NoteItem className="note-item" note={noteData} showAvatar={false} user_id={participantId} />
          </NoteContainer>
          <Subdescription mt="20px">{label}</Subdescription>
          <Box>
            <CommentInputContainer>
              <StyledTextField
                id="message"
                placeholder="Write your message here"
                maxRows={2}
                rows={1}
                value={fields?.message}
                onChange={onElementChange}
                error={Boolean(errors.message)}
                errorText={errors.message}
                disabled={false}
              />
            </CommentInputContainer>
          </Box>
          <ActionButtonsContainer>
            <Button variant="outline-primary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              disabled={!fields?.message?.length}
              variant="primary"
              isLoading={isProcessing}
              onClick={() => {
                if (isProcessing) return

                if (fields?.message?.length) onContinue()
              }}
            >
              Unflag Member
            </Button>
          </ActionButtonsContainer>
        </>
      )}
    </UnflagDialogBodyContainer>
  )
}

const WrappedUnflagDialog = flowRight(withDialog())(UnflagDialog)

const Title = styled(Box)`
  line-height: 24px;
  font-size: 20px;
  font-weight: 700;
  display: flex;

  .tooltip_flag {
    margin-left: 5px;
  }
`

const StyledTextField = styled(TextField)<{ id?: string }>`
  width: 100%;

  ${({ id }) => `
    textarea#${id} {
      padding: 16px 54px 16px 16px;
    }
  `}
`

const StyledButton = styled(Button)`
  padding: 1rem 1.5rem;
  margin: 40px auto 0;

  .spinner {
    margin-right: 10px;
  }
`

const CommentInputContainer = styled(Box)`
  position: relative;
  display: flex;
  align-items: flex-start;

  .avatar-container {
    width: 48px;
    height: 48px;
    min-width: 48px;
    min-height: 48px;

    margin-right: 16px;
    margin-top: 8px;
  }
`

const NoteContainer = styled(Box)`
  padding: 20px;
  border-radius: 4px;
  background: #fcfcfc;
  border: 1px solid #dcdcdc;

  .note-item {
    padding: 10px 0;
  }

  &.warn {
    border-left: 10px solid ${FLAG_WARNING_COLOR};
  }
  &.stop {
    border-left: 10px solid ${FLAG_DANGER_COLOR};
  }
`
const DescriptionContainer = styled(Box)`
  display: flex;
`

const Status = styled(Box)`
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
`

const Description = styled(Box)`
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
`

const Subdescription = styled(Box)`
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
`

const UnflagDialogBodyContainer = styled(Box)`
  color: ${({ theme }) => theme.colors.secondary};
  padding: 40px;
  width: 100%;

  .description {
    width: 100%;
    margin-bottom: 40px;
    text-align: center;

    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
  }

  .dialog-button-close {
    font-weight: bold;
    font-size: 18px;
    line-height: 24px;

    color: ${({ theme }) => theme?.colors?.main};
    text-decoration-line: underline;
    text-align: center;
    cursor: pointer;

    width: fit-content;
    margin: 40px auto 0;
  }
`

const ActionButtonsContainer = styled(Box)`
  display: flex;
  place-content: center;
  margin-top: 20px;

  button {
    min-width: 140px;
    padding: 0 20px;

    &:first-child {
      margin-right: 2rem;
    }
  }
`

WrappedUnflagDialog.defaultProps = {
  // continueIsDisabled(data: UnflagDialogForm) {
  //   return !data.confirm
  // },
}

export default WrappedUnflagDialog
